import { useNavigate, useRouteError } from 'react-router-dom';
import typeToString from '@/utils/typeToString';
import { AxiosError, isAxiosError } from 'axios';

import errorImage500 from '@/assets/images/errors/error_500.svg';
import errorImage404 from '@/assets/images/errors/error_404.svg';
import errorImage403 from '@/assets/images/errors/error_403.svg';
import errorImage401 from '@/assets/images/errors/error_401.svg';
import { useMemo } from 'react';
import { Unstable_Grid2 as Grid, Button, ButtonProps, useColorScheme } from '@mui/material';
import DeveloperView from '@/components/common/DeveloperView';
import { services } from '@/constant/configure';

export default (props: any) => {
    const navigate = useNavigate();

    const err: any = useRouteError();
    const { mode } = useColorScheme();

    // 프로필링크 페이지 오류 로깅
    const currentPath = window.location.pathname;
    if (currentPath.includes('/studio/')) {
        console.error('[ProfileLink Error]', {
            path: currentPath,
            error: err,
            status: err?.status || err?.statusText || err?.code,
            message: err?.message || err,
            stack: err?.stack,
            fullError: JSON.stringify(err, Object.getOwnPropertyNames(err)),
        });
    }

    const images: Record<number | string, any> = {
        401: errorImage401,
        403: errorImage403,
        404: errorImage404,
        500: errorImage500,
        502: errorImage500,
    };

    if (err instanceof Error) console.log('This is Error');
    if (err instanceof ErrorEvent) console.log('This is ErrorEvent');
    if (err instanceof EvalError) console.log('This is EvalError');
    if (err instanceof AxiosError) console.log('This is AxiosError');
    if (err instanceof TypeError) console.log('This is TypeError');
    if (err instanceof SyntaxError) console.log('This is SyntaxError');
    if (err instanceof ReferenceError) console.log('This is ReferenceError');
    if (err instanceof AggregateError) console.log('This is AggregateError');

    // 401: Access is denied! / Access is denied due to invalid credentials.
    // 404: Page Not Founded! / 방문하시려는 페이지를 찾을 수 없습니다.
    // 500: Oooops!!! / Internal Server Error

    const errStatus = err?.status || err?.statusText || err?.code;
    const errMessage = !(typeof err === 'string') ? err?.message : err;

    console.log('errStatus', errStatus);

    // const env = process.env.VITE_DEPLOY_ENV;

    const preset: Record<
        number | string,
        {
            title: string;
            message: string;
            actions?: {
                label: string;
                action: () => void;
                color?: ButtonProps['color'];
            }[];
        }
    > = {
        401: {
            title: 'Authorization Required',
            message: '로그인이 필요한 페이지입니다.',
            actions: [
                {
                    label: '로그인 페이지로 이동',
                    action: () => {
                        navigate(`${services.flowing_studio}/login`);
                    },
                },
            ],
        },
        403: {
            title: 'Not Permitted.',
            message: '요청한 페이지에 접근할 권한이 없습니다.',
            actions: [
                {
                    label: '홈페이지로 이동',
                    action: () => {
                        navigate('/');
                    },
                },
                {
                    label: '다시 로그인하기',
                    action: () => {
                        navigate('/logout');
                    },
                    color: 'error',
                },
            ],
        },
        404: {
            title: 'Page Not Founded!',
            message: '요청하신 페이지를 찾을 수 없습니다.',
            actions: [
                {
                    label: '홈페이지로 이동',
                    action: () => {
                        navigate('/');
                    },
                },
                {
                    label: '다시 로그인하기',
                    action: () => {
                        navigate('/logout');
                    },
                    color: 'error',
                },
            ],
        },
        500: {
            title: 'Internal server error',
            message: '서버 상태가 원활하지 않습니다. 잠시 후, 다시 시도해주세요.',
        },
        502: {
            title: 'Internal server error',
            message: '서버 상태가 원활하지 않습니다. 잠시 후, 다시 시도해주세요.',
        },
        ERR_NETWORK: {
            title: 'Network Error',
            message: '네트워크 상태가 원활하지 않습니다.',
        },
    };

    const computedCurrentMessage = useMemo(() => {
        try {
            return preset[errStatus];
        } catch (e) {
            return preset[404];
        }
    }, [errStatus]);

    return (
        <div
            className={'flex-1 flex justify-center items-center p-10 xl:p-44'}
            style={{
                backgroundColor: mode === 'dark' ? '#333' : '#ebebeb',
            }}
        >
            <div
                className={'w-full'}
                style={{
                    maxWidth: 650,
                }}
            >
                <div>
                    <img
                        src={!!err.status && !!images[err.status] ? images[err.status] : errorImage404}
                        alt=""
                        style={{
                            filter: mode === 'dark' ? 'drop-shadow(0 0 15px rgba(255,255,255,0.4))' : undefined,
                        }}
                    />
                </div>

                <div className={'px-10'}>
                    <h1 className={'text-h1 pb-3'}>
                        {!!computedCurrentMessage?.title ? computedCurrentMessage.title : 'Page Not Founded!'}
                    </h1>
                    <p>
                        {!!computedCurrentMessage?.message
                            ? computedCurrentMessage.message
                            : '요청하신 페이지를 찾을 수 없습니다.'}
                    </p>

                    <Grid container sx={{ mt: 3 }}>
                        {!!computedCurrentMessage?.actions &&
                            computedCurrentMessage.actions.map((item, index) => {
                                return (
                                    <Grid xs={12} key={index}>
                                        <Button
                                            variant={'contained'}
                                            color={item.color || 'primary'}
                                            onClick={
                                                !!item.action
                                                    ? () => {
                                                          item.action();
                                                      }
                                                    : undefined
                                            }
                                            fullWidth
                                        >
                                            {item.label || ''}
                                        </Button>
                                    </Grid>
                                );
                            })}
                    </Grid>
                </div>

                <DeveloperView>
                    <div
                        className={'mt-10'}
                        style={{
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            borderRadius: 15,
                            padding: 20,
                        }}
                    >
                        <div>{err.status || err.statusText || err.message}</div>
                        {!err?.status && !err?.statusText && <div>{JSON.stringify(err)}</div>}
                    </div>
                </DeveloperView>
            </div>
        </div>
    );
};
